import { useQuery } from "@tanstack/react-query";
import { useGetList, type Identifier } from "ra-core";
import { useCallback, useEffect, useId } from "react";

import { getSupabaseClient } from "../../providers/supabase/supabase";
import { useSocialInbox } from "../social/useSocial";
import type { WaMessage, WaThread } from "./whatsapp";

/** Reloads when a message arrives or a thread changes (live). */
export const useLive = (
  table: string,
  reload: () => void,
  filter?: string,
  enabled = true,
) => {
  const channelName = `wa-${table}-${useId()}`;
  useEffect(() => {
    if (!enabled) return;
    const supabase = getSupabaseClient();
    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table, ...(filter ? { filter } : {}) },
        () => reload(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [channelName, table, filter, reload, enabled]);
};

export const useWaInbox = () => {
  const {
    data = [],
    refetch,
    isPending,
  } = useGetList<WaThread>(
    "gula_wa_inbox",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "last_message_at", order: "DESC" },
    },
    { refetchInterval: 60_000 },
  );
  const reload = useCallback(() => {
    refetch();
  }, [refetch]);
  useLive("gula_wa_threads", reload);
  return {
    threads: data,
    isPending,
    reload,
  };
};

export const useWaMessages = (contactId?: Identifier | null) => {
  const {
    data = [],
    refetch,
    isPending,
  } = useGetList<WaMessage>(
    "gula_wa_messages",
    {
      filter: { contact_id: contactId },
      pagination: { page: 1, perPage: 500 },
      sort: { field: "created_at", order: "ASC" },
    },
    { enabled: contactId != null, refetchInterval: 60_000 },
  );
  const reload = useCallback(() => {
    refetch();
  }, [refetch]);
  useLive(
    "gula_wa_messages",
    reload,
    contactId != null ? `contact_id=eq.${contactId}` : undefined,
    contactId != null,
  );
  return { messages: data, isPending, reload };
};

/** Total unread conversations (WhatsApp, Instagram, Messenger) for the header badge. */
export const useWaUnread = () => {
  const { threads } = useWaInbox();
  const { threads: social } = useSocialInbox();
  return [...threads, ...social].filter((thread) => thread.unread > 0).length;
};

export const markThreadRead = async (contactId: Identifier) => {
  await getSupabaseClient()
    .from("gula_wa_threads")
    .update({ unread: 0 })
    .eq("contact_id", Number(contactId))
    .gt("unread", 0);
};

export class WindowClosedError extends Error {}

export const invokeSend = async (
  body: Record<string, unknown>,
  fallback: string,
  fn = "whatsapp_send",
) => {
  const { data, error } = await getSupabaseClient().functions.invoke(fn, {
    method: "POST",
    body,
  });
  if (error) {
    let details: { message?: string; code?: string } | undefined;
    try {
      details = await (error as { context?: Response }).context?.json();
    } catch {
      // keep the generic message
    }
    if (details?.code === "window_closed")
      throw new WindowClosedError(details.message);
    throw new Error(details?.message ?? fallback);
  }
  return data;
};

/** The approved welcome template (name + call slot): opens the conversation. */
export const sendWhatsappWelcome = (contactId: Identifier) =>
  invokeSend(
    { contact_id: Number(contactId), template: "welcome" },
    "No se ha podido enviar la bienvenida",
  );

export const sendWhatsapp = (contactId: Identifier, text: string) =>
  invokeSend({ contact_id: Number(contactId), text }, "No se ha podido enviar");

/** A file of the materials library (video, prices, photo...). */
export const sendWhatsappMaterial = (
  contactId: Identifier,
  materialId: Identifier,
) =>
  invokeSend(
    { contact_id: Number(contactId), material_id: Number(materialId) },
    "No se ha podido enviar el material",
  );

/** Whether Meta has approved the welcome template (it is switched on). */
export const useWelcomeEnabled = () =>
  useQuery({
    queryKey: ["whatsapp-welcome-status"],
    queryFn: async () => {
      const data = (await invokeSend(
        { action: "welcome_status" },
        "No se ha podido comprobar",
      )) as { enabled?: boolean };
      return data?.enabled === true;
    },
    staleTime: 10 * 60_000,
  }).data === true;
